import React from 'react';
import Header from '@/components/Header';
import { useWhatsAppPopup } from '@/contexts/WhatsAppPopupContext';
import Footer from '@/components/Footer';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Ship, FileText, Sailboat, ListChecks, Clock } from 'lucide-react';

// New infographic components
import JornadaEscalaSection from '@/components/despachante/JornadaEscalaSection';
import AnalisePerformanceSection from '@/components/despachante/AnalisePerformanceSection';

// --- Hero Section (Redesigned with Stat Cards) ---
const HeroDespachante: React.FC = () => {
  const { openPopup } = useWhatsAppPopup();
  return (
    <section className="relative bg-primary text-primary-foreground py-32 lg:py-44 overflow-hidden">
      {/* Background effects */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="w-full h-full bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-gold/10 via-primary to-primary" />
      </div>
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="container mx-auto px-6 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-block px-4 py-1.5 mb-6 rounded-full bg-gold/20 text-gold font-bold tracking-wider text-sm border border-gold/30">
            DOCUMENTAÇÃO NÁUTICA SIMPLIFICADA
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight">
            DESPACHANTE <span className="text-gold">NÁUTICO</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl max-w-3xl mx-auto text-primary-foreground/80 font-light">
            Elimine a burocracia e navegue com segurança jurídica. Resolvemos transferências, renovações e registros com agilidade máxima junto à Marinha.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => openPopup("despachante")} className="btn-gold w-full sm:w-auto text-lg px-8 py-6 rounded-lg">Fale com um Especialista</button>
            <a href="#servicos">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-gold text-gold hover:bg-gold/10 hover:text-gold">
                Conheça os Serviços
              </Button>
            </a>
          </div>
        </motion.div>

        {/* Quick Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-16"
        >
          {[
            { value: '100%', label: 'Regularização' },
            { value: '15-60d', label: 'Prazo Legal' },
            { value: 'Zero', label: 'Burocracia p/ Você' },
            { value: 'Total', label: 'Tranquilidade' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-white/5 backdrop-blur-sm p-5 rounded-xl border border-white/10 hover:border-gold/30 transition-all duration-300"
            >
              <div className="text-3xl font-bold text-gold">{stat.value}</div>
              <div className="text-xs text-primary-foreground/60 uppercase tracking-wider mt-1">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};


// --- Main Services Section (Kept from original) ---
const ServicesSection: React.FC = () => {
  const { openPopup } = useWhatsAppPopup();
  return (
    <section id="servicos" className="py-24 sm:py-32 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
            Nossos Serviços
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary">
            Serviços de Despachante Náutico
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto">
            Soluções completas de documentação para embarcações de esporte e recreio — lanchas, jetskis, veleiros, iates e barcos.
          </p>
        </motion.div>

        <Tabs defaultValue="recreio" className="w-full max-w-5xl mx-auto">
          <TabsList className="grid w-full grid-cols-1 sm:grid-cols-2 bg-cream border border-gold/20">
            <TabsTrigger value="recreio">
              <Sailboat className="w-5 h-5 mr-2" /> Embarcações de Lazer
            </TabsTrigger>
            <TabsTrigger value="habilitacoes">
              <FileText className="w-5 h-5 mr-2" /> Habilitações Náuticas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="recreio" className="py-8">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-lg">Transferência de Propriedade</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <p>O serviço mais comum para lanchas e jetskis. Cuidamos de todo o processo, alertando sobre o <strong>prazo fatal de 15 a 60 dias</strong> após a venda para evitar multas. Cuidamos de reconhecimento de firmas, laudos e protocolo.</p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger className="text-lg">Atualização, 2ª Via e Outros Serviços</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li><strong>Atualização de Motor:</strong> Averbação da troca de motor, com nota fiscal e laudos necessários.</li>
                    <li><strong>2ª Via de TIE/TIEM:</strong> Reposição de documentos perdidos ou extraviados.</li>
                    <li><strong>Renovação de Carteira (CHA):</strong> Para Arrais, Mestre e Capitão Amador. Informamos sobre as regras para renovação sem nova prova.</li>
                    <li><strong>Inclusão de Motonauta:</strong> Já tem Arrais? Inclua a categoria de Motonauta conosco. <a href="/motonauta" className="text-gold underline">Saiba mais</a>.</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger className="text-lg">Assessoria Jurídica e Defesa</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <p>Defesa para proprietários autuados pela fiscalização:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li><strong>Recursos de Multas:</strong> Defesa administrativa contra Autos de Infração da Inspeção Naval.</li>
                    <li><strong>Liberação de Embarcações Apreendidas:</strong> Procedimentos de urgência para regularizar pendências.</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            <div className="mt-8 flex justify-center">
              <button onClick={() => openPopup("despachante")} className="btn-gold px-8 py-6 text-lg rounded-lg">
                Quero Regularizar minha Embarcação
              </button>
            </div>
          </TabsContent>

          <TabsContent value="habilitacoes" className="py-8">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-lg">Renovação de Carteira (CHA)</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <p>A Carteira de Habilitação de Amador (CHA) tem validade de <strong>10 anos</strong> (5 anos para maiores de 65). Na maioria dos casos, a renovação pode ser feita <strong>sem nova prova</strong> — basta atestado médico ou CNH válida, documentos pessoais e pagamento da GRU.</p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger className="text-lg">Inclusão de Categoria (Motonauta)</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <p>Já é Arrais Amador e quer pilotar jet ski? Inclua a categoria <strong>Motonauta</strong> na sua CHA. Cuidamos de todo o processo junto à Capitania dos Portos, sem necessidade de nova prova teórica.</p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger className="text-lg">2ª Via de CHA</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <p>Perdeu ou danificou sua carteira de habilitação náutica? Emitimos a 2ª via com agilidade. Basta apresentar documentos pessoais, BO (se for roubo/furto) e pagar a GRU.</p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            <div className="mt-8 flex justify-center">
              <button onClick={() => openPopup("despachante")} className="btn-gold px-8 py-6 text-lg rounded-lg">
                Renovar ou Incluir Categoria agora
              </button>
            </div>
          </TabsContent>

        </Tabs>
      </div>
    </section>
  );
};


// --- Checklists and Fees Section (Kept from original) ---
const ChecklistsAndFeesSection: React.FC = () => (
  <section className="py-24 sm:py-32 bg-primary/[0.03]">
    <div className="container mx-auto px-6">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
          Documentação
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl text-primary">Transparência e Agilidade</h2>
        <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
          Saiba exatamente o que precisa e quanto custa antes de iniciar o processo.
        </p>
      </div>
      <Tabs defaultValue="checklist" className="w-full max-w-4xl mx-auto">
        <TabsList className="grid w-full grid-cols-1">
          <TabsTrigger value="checklist"><ListChecks className="mr-2" />Checklist de Documentos</TabsTrigger>
        </TabsList>
        <TabsContent value="checklist" className="py-8">
          <Accordion type="single" collapsible>
            <AccordionItem value="transferencia">
              <AccordionTrigger>Documentos para Transferência de Propriedade</AccordionTrigger>
              <AccordionContent>
                <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                  <li>Requerimento ao Capitão dos Portos</li>
                  <li>Comprovante de Residência (cópia autenticada, máx. 90 dias)</li>
                  <li>RG e CPF ou CNH (cópia autenticada)</li>
                  <li>BSADE/BADE preenchido</li>
                  <li>Documento de Compra e Venda (com firmas reconhecidas por autenticidade)</li>
                  <li>TIE/TIEM Original do antigo proprietário</li>
                  <li>Guia de Recolhimento da União (GRU) paga</li>
                  <li>Prova de Propriedade do Motor (se aplicável)</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="renovacao-cha">
              <AccordionTrigger>Documentos para Renovação de CHA (Amador)</AccordionTrigger>
              <AccordionContent>
                <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                  <li>Requerimento padrão</li>
                  <li>Atestado Médico (bom estado psicofísico) ou CNH válida</li>
                  <li>Cópia da CHA vencida</li>
                  <li>RG, CPF e Comprovante de Residência</li>
                  <li>Guia de Recolhimento da União (GRU) paga</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>
    </div>
  </section>
);


// --- Competitive Edge Section (Enhanced) ---
const CompetitiveEdgeSection: React.FC = () => {
  const edges = [
    { icon: <Ship className="w-10 h-10 text-gold" />, title: "Assessoria Completa", description: "Da inscrição à renovação, cuidamos de toda a documentação da sua embarcação junto à Capitania dos Portos e Marinha." },
    { icon: <Clock className="w-10 h-10 text-gold" />, title: "Rastreamento de Vencimentos", description: "Notificamos você via WhatsApp sobre o vencimento de TIE, CHA e seguros com antecedência, evitando multas e dor de cabeça." },
    { icon: <ListChecks className="w-10 h-10 text-gold" />, title: "Atendimento Rápido", description: "Envie cópias digitais para análise prévia. Agilizamos o processo e evitamos viagens desnecessárias à Capitania." }
  ];

  return (
    <section className="py-24 sm:py-32 bg-background">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
            Diferenciais
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl text-primary">Por que escolher a JL?</h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            O que fazemos de diferente da concorrência.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {edges.map((edge, i) => (
            <motion.div
              key={edge.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              whileHover={{ scale: 1.03, y: -5 }}
              className="text-center p-8 border border-border rounded-xl bg-white cursor-default transition-shadow duration-300 hover:shadow-lg"
              style={{ boxShadow: '0 2px 12px hsl(0 0% 0% / 0.04)' }}
            >
              <div className="flex justify-center mb-4">{edge.icon}</div>
              <h3 className="text-xl font-bold text-primary mt-2 mb-3">{edge.title}</h3>
              <p className="text-muted-foreground">{edge.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- FAQ Section ---
const FaqDespachanteSection: React.FC = () => {
  const faqItems = [
    { question: "Comprei um barco usado. Qual o prazo para transferir a documentação?", answer: "O prazo legal varia conforme o tipo de registro: 15 dias para embarcações registradas no Tribunal Marítimo e 60 dias para embarcações inscritas na Capitania dos Portos, contados a partir da data da venda. O descumprimento pode gerar multas. Cuidamos de todo o processo para que você não perca nenhum prazo." },
    { question: "Qual a diferença entre inscrição na Capitania e registro no Tribunal Marítimo?", answer: "A Inscrição na Capitania é para embarcações miúdas ou de médio porte em navegação interior — resulta no TIE (Título de Inscrição). O Registro no Tribunal Marítimo é obrigatório para embarcações com Arqueação Bruta superior a 100 ou com financiamento/hipoteca — resulta na PRPM (Provisão de Registro de Propriedade Marítima). É um processo mais complexo e de alto valor." },
    { question: "Preciso renovar minha Carteira de Habilitação (CHA). É necessário refazer a prova?", answer: "Não, na maioria dos casos. Se a sua CHA não estiver vencida há mais de 2 anos, a renovação pode ser feita sem nova prova — basta apresentar atestado médico (ou CNH válida), documentos pessoais e pagar a GRU. A validade é de 10 anos (5 anos para maiores de 65)." },
    { question: "Minha embarcação foi apreendida pela fiscalização. O que fazer?", answer: "Atuamos com procedimentos de urgência para regularizar as pendências que causaram a apreensão. Realizamos defesa administrativa contra Autos de Infração e recursos de multas junto à Inspeção Naval. Entre em contato imediatamente para uma análise do caso." },
    { question: "Vocês cuidam de licenciamento de rádio VHF na ANATEL?", answer: "Sim! Embarcações equipadas com rádio VHF ou SSB precisam de licença da ANATEL. Realizamos o Licenciamento de Estação e a gestão das taxas FISTEL, garantindo que você esteja em conformidade com as exigências de comunicação marítima." },
    { question: "O seguro DPEM ainda é obrigatório?", answer: "A obrigatoriedade do DPEM (Danos Pessoais Causados por Embarcações) tem variado na legislação recente, mas muitas Capitanias e seguradoras ainda o exigem para emissão e renovação de documentos. Orientamos cada cliente sobre a situação atual e providenciamos a emissão quando necessário." },
    { question: "Quanto tempo leva para regularizar uma embarcação?", answer: "Depende do serviço: uma inscrição simples leva de 10 a 20 dias úteis, uma transferência de propriedade de 15 a 30 dias, e uma renovação de CHA de 8 a 15 dias. Esses prazos são estimativas, pois variam conforme a Capitania regional. Na aba 'Taxas e Prazos' da seção de documentação, você encontra uma tabela detalhada." },
    { question: "Por que contratar um despachante ao invés de fazer eu mesmo?", answer: "A regularização de embarcações envolve formulários técnicos (BADE, GRUs), prazos fatais, reconhecimento de firma por autenticidade e interação com múltiplos órgãos (Marinha, ANATEL, ANVISA, Tribunal Marítimo). Um erro ou atraso pode gerar multas pesadas e até apreensão. O despachante especializado garante conformidade legal e elimina o risco — para que você foque no que importa: navegar." },
  ];

  return (
    <section className="bg-primary py-24 sm:py-32">
      <div className="container mx-auto px-6 lg:px-12">
        <h2 className="text-center font-heading text-3xl sm:text-4xl md:text-5xl text-primary-foreground mb-16">Dúvidas Frequentes</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-b-gold/20">
                <AccordionTrigger className="text-left text-lg text-primary-foreground/90 hover:no-underline hover:text-gold">{item.question}</AccordionTrigger>
                <AccordionContent className="text-primary-foreground/80 text-base">{item.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

// --- CTA Section ---
const CtaSection: React.FC = () => {
  const { openPopup } = useWhatsAppPopup();
  return (
    <section className="py-20 bg-primary/[0.03]">
      <div className="container mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
            Resolução Imediata
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary mb-4">
            Sua única preocupação deve ser o <span className="text-gold">Próximo Destino</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Não deixe sua embarcação parada ou em risco por pendências documentais. Deixe a burocracia com quem entende e garanta sua paz no mar.
          </p>
          <button onClick={() => openPopup("despachante")} className="btn-gold text-xl px-12 py-8 rounded-lg shadow-xl hover:shadow-gold/20 transform hover:-translate-y-1 transition-all">
            Falar com Especialista Agora
          </button>
        </motion.div>
      </div>
    </section>
  );
};

// --- Main Page Component ---
const DespachantePage: React.FC = () => {
  return (
    <>
      <Header />
      <main>
        <HeroDespachante />
        <ServicesSection />
        <JornadaEscalaSection />
        <AnalisePerformanceSection />
        <ChecklistsAndFeesSection />
        <CompetitiveEdgeSection />
        <FaqDespachanteSection />
        <CtaSection />
      </main>
      <Footer />
    </>
  );
};

export default DespachantePage;
